import React, { useState, useMemo } from "react";

import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Check as ConfirmIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  ToggleButton,
  IconButton,
  Stack,
  Paper,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { apiClient } from "../api/client";
import {
  SavePfdRequest,
  SuggestPfdRequest,
  SuggestTermDateRequest,
  EditSuggestedTermDateRequest,
  ConfirmTermDateRequest,
} from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { CACard, CADialog } from "../components/ui";
import { LoadingOverlay } from "../components/ui/LoadingOverlay/LoadingOverlay";
import { useNotification } from "../contexts/NotificationContext";
import { useServicePermissions, PartnerPortalAcl } from "../hooks";
import { useAppSelector } from "../store/hooks";

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);

const selectCurrentServiceId = (state: any) => state.service?.selectedService?.serviceId;
const selectCurrentService = (state: any) => state.service?.selectedService;

type CalendarView = "term1" | "term2" | "term3" | "term4" | "pfd";

interface CalendarDate {
  date: Dayjs;
  type: CalendarView;
  title: string;
  id?: number;
}

const KeyDatesPage: React.FC = () => {
  const currentServiceId = useAppSelector(selectCurrentServiceId);
  const selectedService = useAppSelector(selectCurrentService);
  const { hasServicePermission, hasGlobalPermission } = useServicePermissions();
  const { showSuccess, showError, showWarning } = useNotification();
  const queryClient = useQueryClient();

  // Check if user has TermDates permission
  // Logic matches UserHasServicePermissionAttribute.cs:
  // 1. First check if user has the ACL in global acl claim
  // 2. Then check service-specific permission in partner_service_acls claim
  const hasTermDatesPermission =
    hasGlobalPermission(PartnerPortalAcl.TermDates) ||
    (selectedService?.serviceId ? hasServicePermission(selectedService.serviceId, PartnerPortalAcl.TermDates) : false);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [calendarViews, setCalendarViews] = useState<CalendarView[]>(["term1", "term2", "term3", "term4", "pfd"]);
  const [addPfdOpen, setAddPfdOpen] = useState(false);
  const [newPfdDate, setNewPfdDate] = useState<Dayjs | null>(null);
  const [newPfdType, setNewPfdType] = useState("");
  const [editTermOpen, setEditTermOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<any>(null);
  const [editTermStartDate, setEditTermStartDate] = useState<Dayjs | null>(null);
  const [editTermEndDate, setEditTermEndDate] = useState<Dayjs | null>(null);
  const [editTermFinishTime, setEditTermFinishTime] = useState("03:00 PM");
  const [editTermReason, setEditTermReason] = useState("Curriculum (PFD) Day");
  const [editTermOtherReason, setEditTermOtherReason] = useState("");

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [termToConfirm, setTermToConfirm] = useState<any>(null);
  const [editPfdOpen, setEditPfdOpen] = useState(false);
  const [editingPfd, setEditingPfd] = useState<any>(null);
  const [editPfdDate, setEditPfdDate] = useState<Dayjs | null>(null);
  const [editPfdType, setEditPfdType] = useState("");

  // Query key constant for keyDatesViewModel (defined after state initialization)
  const KEY_DATES_VIEW_MODEL_QUERY_KEY = ["keyDatesViewModel", currentServiceId, selectedYear];

  // Main consolidated query
  const {
    data: keyDatesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: KEY_DATES_VIEW_MODEL_QUERY_KEY,
    queryFn: () => apiClient.keyDates.getKeyDatesViewModel(currentServiceId, selectedYear),
    enabled: !!currentServiceId,
    staleTime: 60 * 1000, // 1 minute
  });

  // Helper function to invalidate key dates query
  const invalidateKeyDatesQuery = () => {
    queryClient.invalidateQueries({ queryKey: KEY_DATES_VIEW_MODEL_QUERY_KEY });
  };

  // Mutation for saving PFD
  const savePfdMutation = useMutation({
    mutationFn: (request: SavePfdRequest) => apiClient.keyDates.savePfd(currentServiceId, request),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Pupil Free Day saved successfully");
    },
    onError: (error) => {
      console.error("Error saving PFD:", error);
      showError("Failed to save Pupil Free Day. Please try again.");
    },
  });

  // Mutation for suggesting PFD (add new PFD functionality)
  const suggestPfdMutation = useMutation({
    mutationFn: (request: SuggestPfdRequest) => apiClient.keyDates.suggestPfd(currentServiceId, request),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Pupil Free Day suggested successfully");
    },
    onError: (error) => {
      console.error("Error suggesting PFD:", error);
      showError("Failed to suggest Pupil Free Day. Please try again.");
    },
  });

  // Mutation for suggesting term date (edit functionality)
  const suggestTermDateMutation = useMutation({
    mutationFn: (request: SuggestTermDateRequest) => apiClient.keyDates.suggestTermDate(currentServiceId, request),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Term dates updated successfully");
    },
    onError: (error) => {
      console.error("Error saving term date:", error);
      showError("Failed to update term dates. Please try again.");
    },
  });

  // Mutation for editing suggested term date
  const editSuggestedTermDateMutation = useMutation({
    mutationFn: (request: EditSuggestedTermDateRequest) =>
      apiClient.keyDates.editSuggestedTermDate(currentServiceId, request),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Term date suggestion updated successfully");
    },
    onError: (error) => {
      console.error("Error updating term date suggestion:", error);
      showError("Failed to update term date suggestion. Please try again.");
    },
  });

  // Mutation for confirming term date
  const confirmTermDateMutation = useMutation({
    mutationFn: (request: ConfirmTermDateRequest) => apiClient.keyDates.confirmTermDate(currentServiceId, request),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Term dates confirmed successfully");
    },
    onError: (error) => {
      console.error("Error confirming term date:", error);
      showError("Failed to confirm term dates. Please try again.");
    },
  });

  // Mutation for deleting PFD
  const deletePfdMutation = useMutation({
    mutationFn: (pfdId: number) => apiClient.keyDates.deletePfd(currentServiceId, pfdId),
    onSuccess: () => {
      invalidateKeyDatesQuery();
      showSuccess("Pupil Free Day deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting PFD:", error);
      showError("Failed to delete Pupil Free Day. Please try again.");
    },
  });

  // Helper function to extract data from keyDatesViewModel
  const extractKeyDatesData = () => {
    return {
      confirmedTermDates: keyDatesData?.confirmedTermDates || [],
      defaultTermDates: keyDatesData?.defaultTermDates || [],
      suggestedTermDates: keyDatesData?.suggestedTermDates || [],
      pupilFreeDays: keyDatesData?.pupilFreeDays || [],
      suggestedPfds: keyDatesData?.suggestedPfds || [],
      pfdServiceTypes: keyDatesData?.pfdServiceTypes || [],
    };
  };

  // Extract data from the consolidated response
  const { confirmedTermDates, defaultTermDates, suggestedTermDates, pupilFreeDays, suggestedPfds, pfdServiceTypes } =
    extractKeyDatesData();
  const currentYearTermsConfirmed = keyDatesData?.currentYearTermsConfirmed ?? false;

  // Prepare calendar dates based on the current view
  const getCalendarDates = (): CalendarDate[] => {
    const dates: CalendarDate[] = [];

    // Add term dates
    [...suggestedTermDates, ...confirmedTermDates, ...defaultTermDates].forEach((term) => {
      if (term.firstDayOfTerm && term.lastDayOfTerm) {
        // Use startOf('day') to ensure we're working with date-only values
        const startDate = dayjs(term.firstDayOfTerm).startOf("day");
        const endDate = dayjs(term.lastDayOfTerm).startOf("day");
        const termView = `term${term.termNumber}` as CalendarView;

        // Add all days in the term range
        let currentDate = startDate.clone();
        while (currentDate.isSameOrBefore(endDate)) {
          dates.push({
            date: currentDate.clone(),
            type: termView,
            title: `Term ${term.termNumber}`,
            id: term.termDateId,
          });

          currentDate = currentDate.add(1, "day").startOf("day");
        }
      }
    });

    // Add pupil free days
    [...pupilFreeDays, ...suggestedPfds].forEach((pfd) => {
      if (pfd.date) {
        const pfdDate = dayjs(pfd.date);
        if (pfdDate.year() === selectedYear) {
          const pfdId = "pupilFreeDayServiceId" in pfd ? pfd.pupilFreeDayServiceId : pfd.pupilFreeDaySuggestionId;
          dates.push({
            date: pfdDate,
            type: "pfd",
            title: "Pupil Free Day",
            id: pfdId,
          });
        }
      }
    });

    return dates;
  };

  const calendarDates = useMemo(() => {
    return getCalendarDates();
  }, [confirmedTermDates, defaultTermDates, pupilFreeDays, suggestedPfds, selectedYear]);

  // Custom day renderer for the calendar
  const CustomDay = (props: any) => {
    const { day, ...other } = props;
    // Use startOf('day') to ensure we're comparing just the date part, not time
    const dayEvents = calendarDates.filter((d) => d.date.startOf("day").isSame(day.startOf("day"), "day"));

    // Prioritize PFD events over other events
    const pfdEvent = dayEvents.find((e) => e.type === "pfd");
    const dayData = pfdEvent || dayEvents[0]; // Use PFD if exists, otherwise first event

    const isHighlighted = dayData && calendarViews.includes(dayData.type);

    let dayColor = "default";
    if (dayData) {
      switch (dayData.type) {
        case "term1":
          dayColor = "#1976d2"; // Blue
          break;
        case "term2":
          dayColor = "#388e3c"; // Green
          break;
        case "term3":
          dayColor = "#f57c00"; // Orange
          break;
        case "term4":
          dayColor = "#7b1fa2"; // Purple
          break;
        case "pfd":
          dayColor = "#d32f2f"; // Red
          break;
      }
    }

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(isHighlighted && {
            backgroundColor: dayColor,
            color: "white",
            "&:hover": {
              backgroundColor: dayColor,
            },
          }),
        }}
      />
    );
  };

  // Handle year change
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  // Handle calendar view toggle (multiple selection)
  const handleCalendarViewToggle = (view: CalendarView) => {
    setCalendarViews((prev) => (prev.includes(view) ? prev.filter((v) => v !== view) : [...prev, view]));
  };

  // Handle term date actions
  const handleEditTermDate = (term: any) => {
    // Check if term has commenced (last day of term is in the past)
    const termLastDate = dayjs(term.lastDayOfTerm);
    const today = dayjs();

    if (termLastDate.isBefore(today, "day")) {
      showWarning(
        "As the term has commenced you will need to contact your Regional Manager to update the term dates, thank you.",
      );
      return;
    }

    setEditingTerm(term);
    setEditTermStartDate(dayjs(term.firstDayOfTerm));
    setEditTermEndDate(dayjs(term.lastDayOfTerm));
    setEditTermFinishTime(term.finishTimeOnLastDayOfTerm || "03:00 PM");
    setEditTermReason(term.reason || "Curriculum (PFD) Day");
    setEditTermOtherReason(
      term.reason && term.reason !== "Curriculum (PFD) Day" && term.reason !== "Change to Public Holidays"
        ? term.reason
        : "",
    );
    setEditTermOpen(true);
  };

  const handleSaveTermDate = async () => {
    if (!editTermStartDate || !editTermEndDate || !editingTerm || !currentServiceId) return;

    // Validate dates are in correct year
    if (editTermStartDate.year() !== selectedYear || editTermEndDate.year() !== selectedYear) {
      showError(`Dates must be in the calendar year ${selectedYear}`);
      return;
    }

    // Validate start date is before end date
    if (editTermStartDate.isAfter(editTermEndDate)) {
      showError("Last day of term must be greater than first day of term");
      return;
    }

    // Determine if we're editing an existing suggestion or creating a new one
    const isEditingExistingSuggestion = editingTerm?.termDateSuggestionId;

    const finalReason = editTermReason === "Other" ? editTermOtherReason : editTermReason;

    try {
      if (isEditingExistingSuggestion) {
        // Edit existing suggestion
        const editRequest = new EditSuggestedTermDateRequest({
          termDateSuggestionId: editingTerm.termDateSuggestionId,
          startDate: editTermStartDate,
          endDate: editTermEndDate,
          termNumber: editingTerm.termNumber,
          reason: finalReason || "Updated via Partner Portal",
        });
        console.log("Editing Term Date - Request being sent to Gateway API:", {
          serviceId: currentServiceId,
          endpoint: "PUT /api/v1/KeyDates/term-dates/{serviceId}/suggest",
          request: editRequest,
        });
        await editSuggestedTermDateMutation.mutateAsync(editRequest);
      } else {
        // Create new suggestion from default term date
        const suggestRequest = new SuggestTermDateRequest({
          termDateId: editingTerm.termDateId,
          startDate: editTermStartDate,
          endDate: editTermEndDate,
          termNumber: editingTerm.termNumber,
          reason: finalReason || "Updated via Partner Portal",
        });
        console.log("Suggesting New Term Date - Request being sent to Gateway API:", {
          serviceId: currentServiceId,
          endpoint: "POST /api/v1/KeyDates/term-dates/{serviceId}/suggest",
          request: suggestRequest,
        });
        await suggestTermDateMutation.mutateAsync(suggestRequest);
      }

      // Close modal and reset form
      setEditTermOpen(false);
      setEditingTerm(null);
      setEditTermStartDate(null);
      setEditTermEndDate(null);
      setEditTermFinishTime("03:00 PM");
      setEditTermReason("Curriculum (PFD) Day");
      setEditTermOtherReason("");
      // Refetch data to ensure UI is up to date
      queryClient.invalidateQueries({ queryKey: ["keyDatesViewModel"] });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleCancelTermEdit = () => {
    setEditTermOpen(false);
    setEditingTerm(null);
    setEditTermStartDate(null);
    setEditTermEndDate(null);
  };

  const handleConfirmTermDate = async (term: any) => {
    setTermToConfirm(term);
    setConfirmDialogOpen(true);
  };

  const confirmTermDate = async () => {
    if (!currentServiceId || !termToConfirm) return;

    const request = new ConfirmTermDateRequest({
      termDateId: termToConfirm.termDateId || termToConfirm.termDateSuggestionId,
      startDate: dayjs(termToConfirm.firstDayOfTerm),
      endDate: dayjs(termToConfirm.lastDayOfTerm),
      termNumber: termToConfirm.termNumber,
    });

    try {
      await confirmTermDateMutation.mutateAsync(request);
      setConfirmDialogOpen(false);
      setTermToConfirm(null);
      // Refetch data to ensure UI is up to date
      queryClient.invalidateQueries({ queryKey: ["keyDatesViewModel"] });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleConfirmDialogConfirm = () => {
    confirmTermDate();
  };

  // Handle PFD actions
  const handleAddPfd = () => {
    setAddPfdOpen(true);
  };

  const handleSavePfd = async () => {
    if (!newPfdDate || !newPfdType || !currentServiceId) return;

    const request = new SuggestPfdRequest({
      pfdDate: newPfdDate,
      pfdServiceTypeId: Number(newPfdType),
    });

    try {
      await suggestPfdMutation.mutateAsync(request);
      // Close modal and reset form
      setAddPfdOpen(false);
      setNewPfdDate(null);
      setNewPfdType("");
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleCancelPfd = () => {
    setAddPfdOpen(false);
    setNewPfdDate(null);
    setNewPfdType("");
  };

  const handleEditPfd = (pfd: any) => {
    setEditingPfd(pfd);
    setEditPfdDate(dayjs(pfd.date));
    setEditPfdType(pfd.pupilFreeDayServiceTypeId.toString());
    setEditPfdOpen(true);
  };

  const handleSaveEditPfd = async () => {
    if (!editPfdDate || !editPfdType || !editingPfd || !currentServiceId) return;

    const request = new SavePfdRequest({
      pupilFreeDaySuggestionId:
        "pupilFreeDayServiceId" in editingPfd ? editingPfd.pupilFreeDayServiceId : editingPfd.pupilFreeDaySuggestionId,
      date: editPfdDate,
      serviceTypeId: Number(editPfdType),
    });

    try {
      await savePfdMutation.mutateAsync(request);
      // Close modal and reset form
      setEditPfdOpen(false);
      setEditingPfd(null);
      setEditPfdDate(null);
      setEditPfdType("");
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const handleCancelEditPfd = () => {
    setEditPfdOpen(false);
    setEditingPfd(null);
    setEditPfdDate(null);
    setEditPfdType("");
  };

  const handleDeletePfd = async (pfdId: number) => {
    if (window.confirm("Are you sure you want to delete this Pupil Free Day?")) {
      try {
        await deletePfdMutation.mutateAsync(pfdId);
      } catch (error) {
        // Error handling is done in the mutation's onError callback
      }
    }
  };

  // Show error state
  if (error) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <CACard>
            <Box sx={{ padding: 2 }}>
              <Alert severity="error">Error loading key dates data. Please try again.</Alert>
            </Box>
          </CACard>
        </Box>
      </MainLayout>
    );
  }

  // No service selected
  if (!currentServiceId) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <CACard>
            <Box sx={{ padding: 2 }}>
              <Alert severity="warning">Please select a service to view key dates.</Alert>
            </Box>
          </CACard>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <LoadingOverlay open={isLoading} message="Loading key dates..." />
      <Box sx={{ p: 3 }}>
        {/* Header with year selector - Mobile responsive */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography variant="h4" component="h1">
            Term Dates & Pupil Free Days
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={(e) => handleYearChange(Number(e.target.value))}>
              {[dayjs().year(), dayjs().year() + 1].map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Warning for unconfirmed term dates */}
        {!currentYearTermsConfirmed && selectedYear === dayjs().year() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Unconfirmed Term Dates</strong>
            <br />
            Please submit/confirm current year term dates to continue using all the site features.
          </Alert>
        )}

        {/* Responsive layout: Pupil Free Days and Term Dates */}
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexDirection: { xs: "column", md: "row" } }}>
          {/* Pupil Free Days Cards */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CACard>
              <Box sx={{ padding: 2, width: "100%", boxSizing: "border-box" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6">Pupil Free Days - {selectedYear}</Typography>
                  {hasTermDatesPermission && (
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleAddPfd}>
                      Add PFD
                    </Button>
                  )}
                </Box>
                {pupilFreeDays.length === 0 && suggestedPfds.length === 0 ? (
                  <Typography color="text.secondary">No pupil free days for {selectedYear}</Typography>
                ) : (
                  <Stack spacing={1}>
                    {/* Confirmed PFDs */}
                    {pupilFreeDays.map((pfd) => (
                      <Paper
                        key={pfd.pupilFreeDayServiceId}
                        variant="outlined"
                        sx={{
                          p: 2,
                          overflow: "hidden",
                          borderRadius: "16px",
                          border: "2px solid",
                          borderColor: "grey.300",
                          transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                          "&:hover": { boxShadow: 4, borderColor: "primary.main" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="caption" color="text.secondary">
                              Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dayjs(pfd.date).format("DD/MM/YYYY")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              Type
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {pfd.pupilFreeDayServiceTypeName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                            <Chip label="Confirmed" color="success" size="small" />
                          </Box>
                        </Box>
                      </Paper>
                    ))}

                    {/* Suggested PFDs */}
                    {suggestedPfds
                      .filter((pfd) => !pfd.deletedOn)
                      .map((pfd) => (
                        <Paper
                          key={pfd.pupilFreeDaySuggestionId}
                          variant="outlined"
                          sx={{
                            p: 2,
                            overflow: "hidden",
                            borderRadius: "16px",
                            border: "2px solid",
                            borderColor: "grey.300",
                            transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                            "&:hover": { boxShadow: 4, borderColor: "primary.main" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                              <Typography variant="caption" color="text.secondary">
                                Date
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dayjs(pfd.date).format("DD/MM/YYYY")}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Type
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {pfd.pupilFreeDayServiceTypeName}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                              <Chip label="Suggested" color="warning" size="small" />
                              {hasTermDatesPermission && (
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  <IconButton size="small" onClick={() => handleEditPfd(pfd)} title="Edit">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeletePfd(pfd.pupilFreeDaySuggestionId)}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                  </Stack>
                )}
              </Box>
            </CACard>
          </Box>

          {/* Term Dates Cards */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CACard>
              <Box sx={{ padding: 2, width: "100%", boxSizing: "border-box" }}>
                {defaultTermDates.length === 0 && suggestedTermDates.length === 0 ? (
                  <Typography color="text.secondary">No term dates for {selectedYear}</Typography>
                ) : (
                  <Stack spacing={1}>
                    {/* Display one card per term (1-4) with the most relevant status */}
                    {[1, 2, 3, 4].map((termNumber) => {
                      // Find the most relevant term data for this term number
                      const confirmedTerm = confirmedTermDates.find((t) => t.termNumber === termNumber);
                      const suggestedTerm = suggestedTermDates.find((t) => t.termNumber === termNumber);
                      const defaultTerm = defaultTermDates.find((t) => t.termNumber === termNumber);

                      // Determine which term data to display (priority: confirmed > suggested > default)
                      const displayTerm = confirmedTerm || suggestedTerm || defaultTerm;

                      if (!displayTerm) return null;

                      // Determine status and actions based on term state
                      let status = { label: "Unknown", color: "default" as any, actions: [] as any[] };

                      if (confirmedTerm) {
                        status = {
                          label: "Confirmed",
                          color: "success",
                          actions: [], // No actions for confirmed terms
                        };
                      } else if (suggestedTerm) {
                        const defaultForTerm = defaultTermDates.find((t) => t.termNumber === termNumber);
                        const defaultInfo = defaultForTerm
                          ? `Default: ${defaultForTerm.state} - ${dayjs(defaultForTerm.firstDayOfTerm).format("DD MMM")} - ${dayjs(defaultForTerm.lastDayOfTerm).format("DD MMM")}`
                          : "";
                        status = {
                          label: `Submitted (${defaultInfo})`,
                          color: "info",
                          actions: hasTermDatesPermission
                            ? [
                                <IconButton
                                  key="edit"
                                  size="small"
                                  onClick={() => handleEditTermDate(suggestedTerm)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>,
                              ]
                            : [],
                        };
                      } else if (defaultTerm) {
                        status = {
                          label: "Unconfirmed",
                          color: "warning",
                          actions: hasTermDatesPermission
                            ? [
                                <IconButton
                                  key="edit"
                                  size="small"
                                  onClick={() => handleEditTermDate(defaultTerm)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>,
                                <IconButton
                                  key="confirm"
                                  size="small"
                                  onClick={() => handleConfirmTermDate(defaultTerm)}
                                  title="Confirm"
                                >
                                  <ConfirmIcon fontSize="small" />
                                </IconButton>,
                              ]
                            : [],
                        };
                      }

                      return (
                        <Paper
                          key={termNumber}
                          variant="outlined"
                          sx={{
                            p: 2,
                            overflow: "hidden",
                            borderRadius: "16px",
                            border: "2px solid",
                            borderColor: "grey.300",
                            transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                            "&:hover": { boxShadow: 4, borderColor: "primary.main" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                              <Typography variant="caption" color="text.secondary">
                                Term
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {termNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Date Range
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dayjs(displayTerm.firstDayOfTerm).format("DD MMM").toUpperCase()}
                                {" - "}
                                {dayjs(displayTerm.lastDayOfTerm).format("DD MMM").toUpperCase()}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              {typeof status.label === "string" ? (
                                <Chip
                                  label={status.label}
                                  color={status.color}
                                  size="small"
                                  sx={{
                                    maxWidth: "100%",
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              ) : (
                                status.label
                              )}
                              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexShrink: 0 }}>{status.actions}</Box>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </CACard>
          </Box>
        </Box>

        {/* Calendar Section - 12 Monthly Calendars */}
        <CACard>
          <Box sx={{ padding: 2, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Typography variant="h6">Calendar View</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                }}
              >
                <ToggleButton
                  value="term1"
                  selected={calendarViews.includes("term1")}
                  onChange={() => handleCalendarViewToggle("term1")}
                  size="small"
                  color="secondary"
                >
                  Term 1
                </ToggleButton>
                <ToggleButton
                  value="term2"
                  selected={calendarViews.includes("term2")}
                  onChange={() => handleCalendarViewToggle("term2")}
                  size="small"
                  color="success"
                >
                  Term 2
                </ToggleButton>
                <ToggleButton
                  value="term3"
                  selected={calendarViews.includes("term3")}
                  onChange={() => handleCalendarViewToggle("term3")}
                  size="small"
                  color="warning"
                >
                  Term 3
                </ToggleButton>
                <ToggleButton
                  value="term4"
                  selected={calendarViews.includes("term4")}
                  onChange={() => handleCalendarViewToggle("term4")}
                  size="small"
                  color="secondary"
                >
                  Term 4
                </ToggleButton>
                <ToggleButton
                  value="pfd"
                  selected={calendarViews.includes("pfd")}
                  onChange={() => handleCalendarViewToggle("pfd")}
                  size="small"
                  color="error"
                >
                  PFD
                </ToggleButton>
              </Box>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* 12 Monthly Calendars Grid - Mobile responsive */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                    xl: "repeat(4, 1fr)",
                  },
                  gap: 2,
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                {Array.from({ length: 12 }, (_, monthIndex) => (
                  <Box
                    key={monthIndex}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      minHeight: { xs: 300, sm: 320 },
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <Typography variant="h6" align="center" sx={{ mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                      {dayjs().month(monthIndex).year(selectedYear).format("MMMM YYYY")}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      <DateCalendar
                        value={null} // Remove the value to prevent first day highlighting
                        views={["day"]}
                        openTo="day"
                        readOnly
                        slots={{
                          day: CustomDay,
                        }}
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          "& .MuiPickersCalendarHeader-root": {
                            display: "none", // Hide the header since we show month/year above
                          },
                          "& .MuiDayCalendar-root": {
                            width: "100%",
                            maxWidth: "100%",
                            padding: "0 !important",
                          },
                          "& .MuiDayCalendar-weekContainer": {
                            justifyContent: "space-between",
                          },
                          "& .MuiPickersDay-root": {
                            margin: "2px !important",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            width: { xs: "32px", sm: "36px" },
                            height: { xs: "32px", sm: "36px" },
                          },
                          // Remove any default selected day styling
                          "& .MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "transparent !important",
                          },
                          "& .MuiPickersDay-root.Mui-selected:hover": {
                            backgroundColor: "transparent !important",
                          },
                        }}
                        // Force the calendar to show the specific month
                        minDate={dayjs().month(monthIndex).year(selectedYear).date(1)}
                        maxDate={dayjs().month(monthIndex).year(selectedYear).endOf("month")}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </LocalizationProvider>

            {/* Legend */}
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: "#1976d2", borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption">Term 1</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: "#388e3c", borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption">Term 2</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: "#f57c00", borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption">Term 3</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: "#7b1fa2", borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption">Term 4</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: "#d32f2f", borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption">Pupil Free Days</Typography>
              </Box>
            </Box>
          </Box>
        </CACard>

        {/* Add PFD Modal */}
        <CADialog
          open={addPfdOpen}
          onClose={handleCancelPfd}
          title="Add Pupil Free Day"
          maxWidth="sm"
          onConfirm={handleSavePfd}
          confirmText={suggestPfdMutation.isPending ? "Saving..." : "Save"}
          confirmDisabled={!newPfdDate || !newPfdType}
          isLoading={suggestPfdMutation.isPending}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={newPfdDate}
              onChange={(newValue) => setNewPfdDate(newValue)}
              format="DD-MMM-YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 },
                },
              }}
            />
          </LocalizationProvider>
          <FormControl fullWidth>
            <InputLabel>Service Type</InputLabel>
            <Select value={newPfdType} label="Service Type" onChange={(e) => setNewPfdType(e.target.value)}>
              {pfdServiceTypes.map((type) => (
                <MenuItem key={type.pupilFreeDayServiceTypeId} value={type.pupilFreeDayServiceTypeId}>
                  {type.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CADialog>

        {/* Edit Term Date Modal */}
        <CADialog
          open={editTermOpen}
          onClose={handleCancelTermEdit}
          title="Submit New Term Dates"
          maxWidth="md"
          onConfirm={handleSaveTermDate}
          confirmText={
            suggestTermDateMutation.isPending || editSuggestedTermDateMutation.isPending ? "Submitting..." : "Submit"
          }
          cancelText="Close"
          confirmDisabled={!editTermStartDate || !editTermEndDate}
          isLoading={suggestTermDateMutation.isPending || editSuggestedTermDateMutation.isPending}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Term {editingTerm?.termNumber}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="First Day of Term"
                value={editTermStartDate}
                onChange={(newValue) => setEditTermStartDate(newValue)}
                format="DD-MMM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="Last Day of Term"
                value={editTermEndDate}
                onChange={(newValue) => setEditTermEndDate(newValue)}
                format="DD-MMM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Finish Time on Last Day of Term</InputLabel>
              <Select
                value={editTermFinishTime}
                onChange={(e) => setEditTermFinishTime(e.target.value)}
                label="Finish Time on Last Day of Term"
              >
                <MenuItem value="12:00 PM">12:00 PM</MenuItem>
                <MenuItem value="12:05 PM">12:05 PM</MenuItem>
                <MenuItem value="12:10 PM">12:10 PM</MenuItem>
                <MenuItem value="12:15 PM">12:15 PM</MenuItem>
                <MenuItem value="12:20 PM">12:20 PM</MenuItem>
                <MenuItem value="12:25 PM">12:25 PM</MenuItem>
                <MenuItem value="12:30 PM">12:30 PM</MenuItem>
                <MenuItem value="12:35 PM">12:35 PM</MenuItem>
                <MenuItem value="12:40 PM">12:40 PM</MenuItem>
                <MenuItem value="12:45 PM">12:45 PM</MenuItem>
                <MenuItem value="12:50 PM">12:50 PM</MenuItem>
                <MenuItem value="12:55 PM">12:55 PM</MenuItem>
                <MenuItem value="01:00 PM">01:00 PM</MenuItem>
                <MenuItem value="01:05 PM">01:05 PM</MenuItem>
                <MenuItem value="01:10 PM">01:10 PM</MenuItem>
                <MenuItem value="01:15 PM">01:15 PM</MenuItem>
                <MenuItem value="01:20 PM">01:20 PM</MenuItem>
                <MenuItem value="01:25 PM">01:25 PM</MenuItem>
                <MenuItem value="01:30 PM">01:30 PM</MenuItem>
                <MenuItem value="01:35 PM">01:35 PM</MenuItem>
                <MenuItem value="01:40 PM">01:40 PM</MenuItem>
                <MenuItem value="01:45 PM">01:45 PM</MenuItem>
                <MenuItem value="01:50 PM">01:50 PM</MenuItem>
                <MenuItem value="01:55 PM">01:55 PM</MenuItem>
                <MenuItem value="02:00 PM">02:00 PM</MenuItem>
                <MenuItem value="02:05 PM">02:05 PM</MenuItem>
                <MenuItem value="02:10 PM">02:10 PM</MenuItem>
                <MenuItem value="02:15 PM">02:15 PM</MenuItem>
                <MenuItem value="02:20 PM">02:20 PM</MenuItem>
                <MenuItem value="02:25 PM">02:25 PM</MenuItem>
                <MenuItem value="02:30 PM">02:30 PM</MenuItem>
                <MenuItem value="02:35 PM">02:35 PM</MenuItem>
                <MenuItem value="02:40 PM">02:40 PM</MenuItem>
                <MenuItem value="02:45 PM">02:45 PM</MenuItem>
                <MenuItem value="02:50 PM">02:50 PM</MenuItem>
                <MenuItem value="02:55 PM">02:55 PM</MenuItem>
                <MenuItem value="03:00 PM">03:00 PM</MenuItem>
                <MenuItem value="03:05 PM">03:05 PM</MenuItem>
                <MenuItem value="03:10 PM">03:10 PM</MenuItem>
                <MenuItem value="03:15 PM">03:15 PM</MenuItem>
                <MenuItem value="03:20 PM">03:20 PM</MenuItem>
                <MenuItem value="03:25 PM">03:25 PM</MenuItem>
                <MenuItem value="03:30 PM">03:30 PM</MenuItem>
                <MenuItem value="03:35 PM">03:35 PM</MenuItem>
                <MenuItem value="03:40 PM">03:40 PM</MenuItem>
                <MenuItem value="03:45 PM">03:45 PM</MenuItem>
                <MenuItem value="03:50 PM">03:50 PM</MenuItem>
                <MenuItem value="03:55 PM">03:55 PM</MenuItem>
                <MenuItem value="04:00 PM">04:00 PM</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Reason for Change</InputLabel>
              <Select
                value={editTermReason}
                onChange={(e) => setEditTermReason(e.target.value)}
                label="Reason for Change"
              >
                <MenuItem value="Curriculum (PFD) Day">Curriculum (PFD) Day</MenuItem>
                <MenuItem value="Change to Public Holidays">Change to Public Holidays</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {editTermReason === "Other" && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Other Reason</InputLabel>
                <Select
                  value={editTermOtherReason}
                  onChange={(e) => setEditTermOtherReason(e.target.value)}
                  label="Other Reason"
                >
                  <MenuItem value="">Please specify...</MenuItem>
                  <MenuItem value="Staff training">Staff training</MenuItem>
                  <MenuItem value="Facility maintenance">Facility maintenance</MenuItem>
                  <MenuItem value="Special event">Special event</MenuItem>
                  <MenuItem value="Custom reason">Custom reason</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </CADialog>

        {/* Confirmation Dialog */}
        <CADialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          title="Confirm Term Date"
          maxWidth="sm"
          onConfirm={handleConfirmDialogConfirm}
          confirmText={confirmTermDateMutation.isPending ? "Confirming..." : "Confirm"}
          confirmColor="secondary"
          isLoading={confirmTermDateMutation.isPending}
        >
          <Typography>Are you sure you want to confirm the term dates? This action cannot be undone.</Typography>
        </CADialog>

        {/* Edit PFD Modal */}
        <CADialog
          open={editPfdOpen}
          onClose={handleCancelEditPfd}
          title="Edit Pupil Free Day"
          maxWidth="sm"
          onConfirm={handleSaveEditPfd}
          confirmText={savePfdMutation.isPending ? "Saving..." : "Save"}
          confirmDisabled={!editPfdDate || !editPfdType}
          isLoading={savePfdMutation.isPending}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={editPfdDate}
              onChange={(newValue) => setEditPfdDate(newValue)}
              format="DD-MMM-YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 },
                },
              }}
            />
          </LocalizationProvider>
          <FormControl fullWidth>
            <InputLabel>Service Type</InputLabel>
            <Select value={editPfdType} label="Service Type" onChange={(e) => setEditPfdType(e.target.value)}>
              {pfdServiceTypes.map((type) => (
                <MenuItem key={type.pupilFreeDayServiceTypeId} value={type.pupilFreeDayServiceTypeId}>
                  {type.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CADialog>
      </Box>
    </MainLayout>
  );
};

export default KeyDatesPage;
